import { Menu } from '@mui/icons-material';
import { IconButton, LinearProgress } from '@mui/material';
import { Grid } from '@mui/system';
import { useSelector } from 'react-redux';
import slugify from 'slugify';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { WORKBOARD } from 'dashboard409a/common/constants';
import { REPORT_DATE } from 'dashboard409a/common/constants/layouts';
import { WORKBOARD_TAB_TITLES } from 'dashboard409a/common/constants/urls';
import { Breadcrumb, DateFilter, Navigation, Title } from 'dashboard409a/components';
import { COMPANY_DETAIL_SLUGS, useSlugDetector, WORKBOARD_SLUGS } from 'dashboard409a/hooks/useSlugDetector';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { RootStateType } from 'dashboard409a/states/store';
import { ReportStatusBadge } from './components/Badges';
import {
  BreadcrumbContainer,
  FilterContainer,
  MainContainer,
  MenuContainer,
  NavigationContainer,
  ProgressBarContainer,
  StyledAppBar,
  StyledToolbar,
} from './styles';
import { IHeader } from './types';

const Header = ({ isLoading, navigationItems, pageTitle }: IHeader) => {
  const { loading, currentValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(
    state => state.valuation
  );
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const isSuperuser = user?.is_superuser;
  const isStaff = user?.is_staff;
  const isApprovedForClients = currentValuationScalarOpinion?.approved_for_clients ?? false;
  const isCompanyUserAndIsNotApprovedForClients = !isSuperuser && !isStaff && !isApprovedForClients;

  const { currentSlug, slugState, location } = useSlugDetector();

  // Check if user is on Workboard URL (workboard/{tab_slug})
  const isOnWorkboard = location.pathname.startsWith('/workboard/');

  // Get analyst tab title based on current slug using dictionary
  const getAnalystTabTitle = (slug: string): string => WORKBOARD_TAB_TITLES[slug] || WORKBOARD;

  // Set the appropriate title based on slug state and user type
  const displayTitle = WORKBOARD_SLUGS.includes(slugState) ? WORKBOARD : pageTitle;

  // Determine if DateFilter should be shown based on slug state
  const shouldShowDateFilterComponent =
    !WORKBOARD_SLUGS.includes(slugState) && !COMPANY_DETAIL_SLUGS.includes(slugState);

  return (
    <StyledAppBar position="sticky" elevation={1} color="inherit">
      <StyledToolbar>
        {/* Title and Breadcrumb */}
        <Grid container direction="row" wrap="nowrap" alignItems="center" width="100%">
          {/* Menu hamburger */}
          <MenuContainer>
            <IconButton component="button">
              <Menu />
            </IconButton>
          </MenuContainer>

          <Grid>
            <Title title={displayTitle} />

            <BreadcrumbContainer>
              {isOnWorkboard ? (
                // For Workboard, show only the tab title
                <Breadcrumb
                  navigationItems={[
                    {
                      id: currentSlug,
                      title: getAnalystTabTitle(currentSlug),
                      handleAction: () => {},
                    },
                  ]}
                  currentSlug={currentSlug}
                />
              ) : (
                // For regular users, show full breadcrumb with badge
                <>
                  <Breadcrumb navigationItems={navigationItems} currentSlug={currentSlug} />
                  <ReportStatusBadge
                    isApproved={isApprovedForClients}
                    loading={loading}
                    isCompanyUserAndIsNotApprovedForClients={isCompanyUserAndIsNotApprovedForClients}
                    isSuperuserOrStaff={isSuperuser || isStaff}
                  />
                </>
              )}
            </BreadcrumbContainer>
          </Grid>
        </Grid>

        {shouldShowDateFilterComponent && (
          <MainContainer>
            {/* Filters */}
            <FilterContainer container justifyContent="flex-end">
              <DateFilter label={REPORT_DATE} filterId={slugify(REPORT_DATE)} isLoading={loading} />
            </FilterContainer>
          </MainContainer>
        )}
      </StyledToolbar>

      <Grid container direction="row" wrap="nowrap" alignItems="center">
        <NavigationContainer>
          <Grid>
            <Navigation items={navigationItems} />
          </Grid>

          <Grid>
            <ProgressBarContainer>{isLoading && <LinearProgress color="primary" />}</ProgressBarContainer>
          </Grid>
        </NavigationContainer>
      </Grid>
    </StyledAppBar>
  );
};

export default Header;
